/**
 * ExportButton — Smart Admin replacement R4 (2026-05-17).
 *
 * Service-oriented design per audit Decision 7. Caller supplies:
 *   - filters: the filter object the button echoes back to fetchPage
 *   - fetchPage: (filters, page, limit) => Promise<{ rows, total }>
 *   - mapRows: (rows) => Record<string, unknown>[] (per-row transform
 *              for export columns; ExportButton doesn't know schema)
 *   - columns: string[] of export column headers (in order; passed
 *              to the underlying exportToExcel for header row)
 *   - filename: base filename for the downloaded file
 *   - format: "xlsx" (default) | "csv"
 *
 * The button knows nothing about Events or any specific entity. R5
 * reports will reuse this exact component for member-detail exports,
 * sales-by-rep exports, etc.
 *
 * Pagination loop per audit Decision 6: fetches 1000-row pages until
 * exhausted; hard-capped at 50k rows with a clear error if exceeded.
 * Reuses the same fetchPage path as the table so filters/results
 * stay consistent.
 */

import { useState } from "react";
import { ArrowDownTrayIcon } from "@heroicons/react/24/outline";
import Button from "./Button";
import { useToast } from "../ui/Toast";
import { exportToExcel, exportToCSV } from "../../utils/dataImportExport";
import { exportErrorMessage } from "../../utils/rpcError";

const EXPORT_PAGE_SIZE = 1000;
const EXPORT_HARD_CAP = 50_000;

export interface ExportButtonProps<F, R> {
  /** Filter spec — opaque to ExportButton; echoed back to fetchPage. */
  filters: F;
  /** Caller-supplied paginated fetch. ExportButton iterates pages. */
  fetchPage: (
    filters: F,
    page: number,
    limit: number,
  ) => Promise<{ rows: R[]; total: number }>;
  /**
   * Per-row transform from R (the entity shape) to a flat export object.
   * May be async — R5 (2026-05-17) added Promise support so reports can
   * hydrate bulk stats / name lookups before mapping without per-row N+1.
   */
  mapRows: (
    rows: R[],
  ) => Record<string, unknown>[] | Promise<Record<string, unknown>[]>;
  /**
   * Column header list, in display order. Must match the keys produced
   * by mapRows (used as the header row + key order in XLSX/CSV).
   */
  columns: string[];
  /** Base filename (no extension — ExportButton appends .xlsx / .csv). */
  filename: string;
  /** Export format. Defaults to xlsx since the utility is already in the bundle. */
  format?: "xlsx" | "csv";
  /** Optional label override; defaults to "Export". */
  label?: string;
  /** Optional disabled override (caller may want to disable while data is stale). */
  disabled?: boolean;
}

function ExportButtonInner<F, R>({
  filters,
  fetchPage,
  mapRows,
  columns,
  filename,
  format = "xlsx",
  label = "Export",
  disabled = false,
}: ExportButtonProps<F, R>) {
  const { showToast } = useToast();
  const [exporting, setExporting] = useState(false);

  const handleExport = async () => {
    setExporting(true);
    try {
      const allRows: R[] = [];
      let page = 1;
      let total = 0;
      // Iterate up to hard cap. Stop as soon as a page returns fewer
      // than EXPORT_PAGE_SIZE rows (that's the last page).
      let keepGoing = true;
      while (keepGoing) {
        const { rows, total: pageTotal } = await fetchPage(
          filters,
          page,
          EXPORT_PAGE_SIZE,
        );
        if (page === 1) {
          total = pageTotal;
        }
        if (rows.length === 0) {
          keepGoing = false;
        } else {
          allRows.push(...rows);
          if (allRows.length > EXPORT_HARD_CAP) {
            showToast(
              "error",
              `Export exceeds ${EXPORT_HARD_CAP.toLocaleString()} rows. Narrow your filters and try again.`,
            );
            setExporting(false);
            return;
          }
          if (rows.length < EXPORT_PAGE_SIZE) {
            keepGoing = false;
          } else {
            page += 1;
          }
        }
      }

      if (allRows.length === 0) {
        showToast(
          "info",
          "No rows match the current filters — nothing to export.",
        );
        return;
      }

      const mapped = await Promise.resolve(mapRows(allRows));
      const ts = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
      const finalName = `${filename}-${ts}`;

      // exportToCSV / exportToExcel take (data, options) per
      // dataImportExport.ts — R3+R4 post-ship audit finding #3: the
      // earlier (data, name, columns) call shape was accepted by
      // TypeScript only because extra args don't error, but filename
      // fell back to "export" and the `columns` arg was ignored at
      // runtime.
      if (format === "csv") {
        exportToCSV(mapped, { filename: finalName, columns });
      } else {
        exportToExcel(mapped, { filename: finalName, columns });
      }

      const totalLabel =
        total > 0 && total !== allRows.length
          ? `${allRows.length.toLocaleString()} of ${total.toLocaleString()}`
          : allRows.length.toLocaleString();
      showToast("success", `Exported ${totalLabel} rows`);
    } catch (err) {
      console.error("Export failed:", err);
      showToast("error", exportErrorMessage(err));
    } finally {
      setExporting(false);
    }
  };

  return (
    <Button
      variant="secondary"
      size="sm"
      icon={ArrowDownTrayIcon}
      onClick={handleExport}
      disabled={disabled || exporting}
    >
      {exporting ? "Exporting…" : label}
    </Button>
  );
}

// Type-parameterized wrapper. React.FC doesn't preserve generics cleanly,
// so cast through a non-generic component identity.
const ExportButton = ExportButtonInner as <F, R>(
  props: ExportButtonProps<F, R>,
) => ReturnType<typeof ExportButtonInner>;

export default ExportButton;
